import { message } from "antd";
import { FieldArray, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { DesignationAPI } from "../api/DesignationAPI";
import { Company } from "../company/Company";
import { CompanyAPI } from "../company/CompanyAPI";
import { CompanySelector } from "../company/CompanySelector.component";
import { CallerContext } from "../components/common/CallerContext";
import { EBTextAreaV2 } from "../components/common/Form/FormElements";
import {
    EBDateInput,
    EBTextInput,
    FormInputLabel,
} from "../components/common/eb-ui-components";
import { FormikSwitchInput } from "../components/common/input/FormikSwitch.input";
import { EBTextInputV2 } from "../components/common/input/TextLine.input";
import { DesignationSelector } from "../designation/Designation.selector";
import { ApiErrorHelper } from "../helpers/ApiError.helper";
import { Designation } from "../model/Designation";
import { InterviewExperience } from "../model/InterviewExperience";
import { InterviewExperienceRound } from "../model/InterviewExperienceRound";
import { QuestionDl } from "../question/Question.Dl";
import { Question } from "../question/Question.Model";
import { QuestionPickerView } from "../question/QuestionPickerView";
import { User } from "../user/User";

interface RoundsQuestionsProps {
  round: InterviewExperienceRound;
  roundIndex: number;
  user: User;
}

interface QuestionPickerInterviewExperienceContext extends CallerContext {
  round: InterviewExperienceRound;
  questionPush: (questionContext: any) => void;
}

function roundQuestionPickerCallback(
  question: Question,
  callerContext: CallerContext
) {
  let context = callerContext as QuestionPickerInterviewExperienceContext;
  let round = context.round;
  var isQuestionAlreadyPushed = false;
  round &&
    round.questions &&
    round.questions.forEach((roundQuestion) => {
      if (question.id === roundQuestion.question?.id) {
        isQuestionAlreadyPushed = true;
      }
    });
  if (!isQuestionAlreadyPushed) {
    context.questionPush({ question: question });
  } else {
    message.error("Question already added in this round");
  }
}

function AddRoundQuestionsView(props: RoundsQuestionsProps) {
  return (
    <>
      <FieldArray name={`rounds.${props.roundIndex}.questions`}>
        {(arrayHelpers: any) => {
          let callerContext = {
            round: props.round,
            questionPush: arrayHelpers.push,
          };

          return (
            <>
              {props.round.questions && props.round.questions.length > 0 ? (
                <div>
                  <div className="row mt-2">
                    <div className="col">Problems Asked:</div>
                  </div>

                  <div className="row">
                    <div className="col">
                      <ul className="list-group">
                        {props.round.questions.map((qc, index) => {
                          return (
                            <li
                              className="list-group-item d-flex justify-content-between align-items-center"
                              key={qc.question?.id || index}
                            >
                              <QuestionDl ques={qc.question} />
                              <div className="col d-flex justify-content-end">
                                <button
                                  className="btn btn-sm"
                                  type="button"
                                  onClick={() =>
                                    arrayHelpers.move(index, index - 1)
                                  }
                                  disabled={index === 0}
                                >
                                  <i className="fas fa-arrow-up"></i>
                                </button>
                                <button
                                  className="btn btn-sm"
                                  type="button"
                                  onClick={() =>
                                    arrayHelpers.move(index, index + 1)
                                  }
                                  disabled={
                                    index === props.round.questions.length - 1
                                  }
                                >
                                  <i className="fas fa-arrow-down"></i>
                                </button>
                                <button
                                  className="btn btn-sm text-danger"
                                  type="button"
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  <i className="far fa-trash-alt"></i>
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="row pt-2">
                <div className="col">
                  <QuestionPickerView
                    callback={roundQuestionPickerCallback}
                    callerContext={callerContext}
                    uniqueKey={"round" + props.roundIndex}
                    user={props.user}
                  />
                </div>
              </div>
            </>
          );
        }}
      </FieldArray>
    </>
  );
}

interface InterviewRoundsProps {
  rounds?: InterviewExperienceRound[];
  user: User;
}

function InterviewRounds(props: InterviewRoundsProps) {
  return (
    <FieldArray name="rounds">
      {(arrayHelpers: any) => (
        <div>
          {props.rounds &&
            props.rounds.length > 0 &&
            props.rounds.map((round, index) => (
              <div key={index} className="row">
                <div className="col">
                  <div className="card mt-3">
                    <div className="card-header py-2 d-flex flex-row bd-highlight">
                      <div className="flex-fill bd-highlight">
                        <div className="pt-2 h6">
                          Please add round {index + 1} details below:
                        </div>
                      </div>

                      <div className="bd-highlight">
                        <button
                          className="btn"
                          type="button"
                          onClick={() => arrayHelpers.move(index, index - 1)}
                          disabled={index === 0}
                        >
                          <i className="fas fa-arrow-up"></i>
                        </button>
                        <button
                          className="btn"
                          type="button"
                          onClick={() => arrayHelpers.move(index, index + 1)}
                          disabled={
                            !props.rounds || index === props.rounds.length - 1
                          }
                        >
                          <i className="fas fa-arrow-down"></i>
                        </button>
                        <button
                          className="btn text-danger"
                          type="button"
                          onClick={() => arrayHelpers.remove(index)}
                        >
                          <i className="far fa-trash-alt"></i>
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      <input type="hidden" name={`rounds.${index}.id`} />
                      <input
                        type="hidden"
                        name={`rounds.${index}.number`}
                        value={index + 1}
                      />
                      <div className="row">
                        <div className="col">
                          <FormInputLabel
                            label="What type of round it was?"
                            required
                          />
                          <EBTextInputV2
                            name={`rounds.${index}.name`}
                            placeholder="Coding and Problem Solving"
                            helpText=""
                            required
                          />
                        </div>

                        <div className="col">
                          <EBTextInput
                            label="How long was the round? (in minutes)"
                            name={`rounds.${index}.durationInMinutes`}
                            placeholder="60"
                            helpText=""
                            required
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <EBTextAreaV2
                            label="Round Description"
                            name={`rounds.${index}.roundDescription`}
                            maxLength={1000}
                            rows={2}
                            helpText=""
                            placeholder="General decription you might want to add about this round."
                            enablePerformance={true}
                          />
                        </div>
                      </div>
                      <input
                        type="hidden"
                        className="form-control"
                        value={index + 1}
                      />
                      <AddRoundQuestionsView
                        round={round}
                        roundIndex={index}
                        user={props.user}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

          <div className="row mt-3">
            <div className="col">
              <button
                className="btn btn-warning btn-sm"
                type="button"
                onClick={() => arrayHelpers.push({})}
              >
                Add Round
              </button>
            </div>
          </div>
        </div>
      )}
    </FieldArray>
  );
}

interface InterviewExperienceFormProps {
  onSubmit: (ie: InterviewExperience) => Promise<void>;
  initialIE?: InterviewExperience;
  user: User;
}

export function InterviewExperienceForm(props: InterviewExperienceFormProps) {
  let [allCompanies, setAllCompanies] = useState<Company[]>();
  let [allDesignations, setAllDesignations] = useState<Designation[]>();

  useEffect(() => {
    CompanyAPI.fetchAllCompanies().then((companies) => {
      setAllCompanies(companies);
    });
    DesignationAPI.fetchAllDesignations().then((designations) => {
      setAllDesignations(designations);
    });
  }, []);
  return (
    <div>
      <Formik
        initialValues={props.initialIE ?? {}}
        onSubmit={(values, actions) => {
          actions.setSubmitting(true);

          var roundNumber = 1;
          values.rounds &&
            values.rounds.forEach((round) => {
              round.number = roundNumber++;
            });

          props
            .onSubmit(values)
            .catch(function (error) {
              let displayError = ApiErrorHelper.getDisplayErrorMessage(error);
              message.error(displayError);
            })
            .finally(() => {
              actions.setSubmitting(false);
            });
        }}
      >
        {(formProps) => (
          <form onSubmit={formProps.handleSubmit}>
            <div className="row">
              <div className="col-md-4">
                <EBDateInput
                  label="Interview Date:"
                  name="interviewDate"
                  required
                  placeholder="Interview date"
                  helpText=""
                />
              </div>

              <div className="col-md-4">
                {allCompanies && (
                  <>
                    <FormInputLabel label="Company:" required={true} />
                    <CompanySelector
                      name="company"
                      companies={allCompanies}
                      allowCreate={true}
                      required={true}
                    />
                  </>
                )}
              </div>
              <div className="col-md-4">
                {allDesignations && (
                  <>
                    <FormInputLabel label="Designation:" required={true} />
                    <DesignationSelector
                      name="designation"
                      designations={allDesignations}
                      required={true}
                      allowCreate={true}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="card mt-4">
              <div className="card-header pb-0 mb-0 border-bottom-0 bg-transparent">
                <h6 className="mb-0">Role & Location Details</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <EBTextInput
                      label="Role Domain"
                      name="roleDomain"
                      placeholder="e.g. Backend, Frontend, Android"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-3">
                    <EBTextInput
                      label="Level/Seniority"
                      name="level"
                      placeholder="e.g. Senior Engineer, SDE 1"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-3">
                    <EBTextInput
                      label="Location (City)"
                      name="locationCity"
                      placeholder="e.g. Bengaluru, Seattle"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-3">
                    <EBTextInput
                      label="Location (Country)"
                      name="locationCountry"
                      placeholder="e.g. India, USA"
                      helpText=""
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-4">
              <div className="card-header pb-0 mb-0 border-bottom-0 bg-transparent">
                <h6 className="mb-0">Candidate Background</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <EBTextInput
                      label="Years of Experience"
                      name="yearsOfExperience"
                      placeholder="e.g. 2.5"
                      type="number"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-4">
                    <EBTextInput
                      label="Previous Companies"
                      name="previousCompanies"
                      placeholder="e.g. Google, Microsoft"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-4">
                    <EBTextInput
                      label="Previous Roles"
                      name="previousRoles"
                      placeholder="e.g. SDE 2, Consultant"
                      helpText=""
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-12">
                    <EBTextAreaV2
                      label="Background Summary"
                      name="backgroundSummary"
                      maxLength={1000}
                      rows={2}
                      placeholder="Brief summary of the candidate's background"
                      helpText=""
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-4">
              <div className="card-header pb-0 mb-0 border-bottom-0 bg-transparent">
                <h6 className="mb-0">Hiring Process & Timeline</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <EBTextInput
                      label="Hiring Channel"
                      name="hiringChannelType"
                      placeholder="e.g. OnCampus, OffCampus, Referral"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-4">
                    <EBTextInput
                      label="College (if Campus)"
                      name="hiringChannelCollege"
                      placeholder="e.g. IIT Delhi"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-4">
                    <EBTextInput
                      label="Campus Location"
                      name="hiringChannelCampusLocation"
                      placeholder=""
                      helpText=""
                    />
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-3">
                    <EBTextInput
                      label="Applied On"
                      name="timelineAppliedOn"
                      placeholder="e.g. Aug 2023 or 2 weeks ago"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-3">
                    <EBTextInput
                      label="Interviewed On"
                      name="timelineInterviewedOn"
                      placeholder="e.g. Sep 2023"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-3">
                    <EBTextInput
                      label="Offer Received On"
                      name="timelineOfferOn"
                      placeholder="e.g. Oct 2023"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-3">
                    <EBTextInput
                      label="Result Wait Time"
                      name="timelineResultWaitTime"
                      placeholder="e.g. 3 days"
                      helpText=""
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-4">
              <div className="card-header pb-0 mb-0 border-bottom-0 bg-transparent">
                <h6 className="mb-0">Company & Team Info</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <EBTextInput
                      label="Organization/Department"
                      name="orgName"
                      placeholder="e.g. AWS, Azure"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-4">
                    <EBTextInput
                      label="Team Name"
                      name="teamName"
                      placeholder="e.g. Core Infrastructure"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-4">
                    <EBTextInput
                      label="Org Description"
                      name="orgDescription"
                      placeholder="Brief what the org does"
                      helpText=""
                    />
                  </div>
                </div>
              </div>
            </div>

            <InterviewRounds
              rounds={formProps.values.rounds}
              user={props.user}
            />
            <div className="mt-3 ml-2">
              <div className="">
                <FormikSwitchInput
                  name={"receivedOffer"}
                  label="Received offer?"
                />
              </div>

              {formProps.values.receivedOffer ? (
                <div className="">
                  {allDesignations && (
                    <>
                      <FormInputLabel label="Select designation if offer was received for different designation" />
                      <DesignationSelector
                        name="offerDesignation"
                        designations={allDesignations}
                        required={false}
                        allowCreate={true}
                      />
                    </>
                  )}
                </div>
              ) : null}
            </div>

            <div className="card mt-4">
              <div className="card-header pb-0 mb-0 border-bottom-0 bg-transparent">
                <h6 className="mb-0">Experience & Notes</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col">
                    <EBTextAreaV2
                      label="General Notes"
                      name="generalNotes"
                      maxLength={2000}
                      rows={3}
                      placeholder="Any general thoughts, suggestions, or notes"
                      helpText=""
                    />
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-6">
                    <EBTextAreaV2
                      label="Preparation Strategy"
                      name="preparationStrategy"
                      maxLength={2000}
                      rows={3}
                      placeholder="How did you prepare for this?"
                      helpText=""
                    />
                  </div>
                  <div className="col-md-6">
                    <EBTextAreaV2
                      label="Feedback from Company"
                      name="feedbackFromCompany"
                      maxLength={2000}
                      rows={3}
                      placeholder="Any official feedback received from recruiter or interviewers"
                      helpText=""
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="hstack gap-5 mt-4">
              <div>
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={formProps.isSubmitting}
                >
                  {formProps.isSubmitting ? "Saving. Please Wait..." : "Save"}
                </button>
              </div>

              <div>
                <FormikSwitchInput label="Add as Anonymous?" name="isAnon" />
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}
